

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import LeafletMapComponent from './LeafletMapComponent';
import yardsData from '../assets/yards.json';
import { computeZoneStats } from '../utils/mapZoneUtils';
import { usePortalAccess } from '../context/PortalAccessContext';
import { probeYardTier1 } from './yard-layout/probeYardTier1';

const durationOptions = [
    { value: 1, label: '1 hour' }, { value: 6, label: '6 hours' }, { value: 12, label: '12 hours' },
    { value: 24, label: '24 hours' }, { value: 72, label: '3 days' }, { value: 168, label: '1 week' },
    { value: 336, label: '2 weeks' }, { value: 720, label: '1 month' }, { value: 'custom', label: 'Custom' }
];

const VIEW_SIGNAL = 'signal_strength';
const VIEW_SPEED = 'internet_speed';
const SIGNAL_METRICS = [
  { value: 'lte', label: 'LTE signal' },
  { value: 'wifi', label: 'Wi-Fi signal' },
];
const SPEED_COLOR_BY = [
  { value: 'upload', label: 'Upload' },
  { value: 'download', label: 'Download' },
  { value: 'both', label: 'Both' },
];
const SPEED_CONNECTION_FILTERS = [
  { value: 'all', label: 'Wi‑Fi + Cellular' },
  { value: 'wifi', label: 'Wi‑Fi only' },
  { value: 'cellular', label: 'Cellular only' },
];

const HeatMap = () => {
    const { access } = usePortalAccess();
    const portalEmail = (access?.email || '').trim();

    // Deep-link support: a device card can link here with ?androidId=&deviceName=&yard=
    // to open the map straight onto that device's speed-test points.
    const [searchParams] = useSearchParams();
    const initialAndroidId = searchParams.get('androidId') || '';
    const initialDeviceName = searchParams.get('deviceName') || '';
    const initialYard = searchParams.get('yard') || '';

    const [selectedYard, setSelectedYard] = useState(initialYard || (yardsData.length > 0 ? yardsData[0].Yard : ''));
    const [selectedDuration, setSelectedDuration] = useState(initialAndroidId ? 720 : 24);
    const [viewMode, setViewMode] = useState(initialAndroidId ? VIEW_SPEED : VIEW_SIGNAL);
    const [signalMetric, setSignalMetric] = useState('lte');
    const [speedColorBy, setSpeedColorBy] = useState('upload');
    const [speedConnectionFilter, setSpeedConnectionFilter] = useState('all');
    const [speedDeviceOptions, setSpeedDeviceOptions] = useState([]);
    const [selectedSpeedDevice, setSelectedSpeedDevice] = useState(
        initialAndroidId ? { value: initialAndroidId, label: initialDeviceName || initialAndroidId } : null
    );
    const [isLoadingSpeedDevices, setIsLoadingSpeedDevices] = useState(false);
    const [heatmapData, setHeatmapData] = useState([]);
    const [signalData, setSignalData] = useState([]);
    const [speedData, setSpeedData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [towerData, setTowerData] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [drawModeEnabled, setDrawModeEnabled] = useState(false);
    const [drawnPolygon, setDrawnPolygon] = useState([]);
    const [isPenActive, setIsPenActive] = useState(true);
    const [yardTileUrl, setYardTileUrl] = useState(null);

    useEffect(() => {
        if (!selectedYard) { setYardTileUrl(null); return; }
        let cancelled = false;
        const controller = new AbortController();
        probeYardTier1(selectedYard, { signal: controller.signal }).then((available) => {
            if (cancelled) return;
            setYardTileUrl(
                available
                    ? `https://yardimages.copart.com/styles/yard${selectedYard}-lite/style.json`
                    : null
            );
        });
        return () => { cancelled = true; controller.abort(); };
    }, [selectedYard]);

    const yardOptions = useMemo(
        () =>
            yardsData.map((y) => ({
                value: String(y.Yard),
                label: `Yard ${y.Yard} - ${y.City}, ${y.State}`,
            })),
        []
    );
    const selectedYardOption = yardOptions.find((o) => o.value === selectedYard) || yardOptions[0];
    const serverUrl = process.env.REACT_APP_API_SERVER_URL;

    useEffect(() => {
        if (!selectedYard || !serverUrl) {
            setSpeedDeviceOptions([]);
            setSelectedSpeedDevice(null);
            return;
        }
        let cancelled = false;
        const fetchDevicesForYard = async () => {
            setIsLoadingSpeedDevices(true);
            try {
                const yardDevicesUrl =
                    `${serverUrl}/maiApi/analytics/yard/${encodeURIComponent(String(selectedYard))}/devices?timeRange=30%20days&limit=500` +
                    (portalEmail ? `&portal_email=${encodeURIComponent(portalEmail)}` : '');
                const res = await fetch(yardDevicesUrl);
                if (!res.ok) throw new Error('Failed to fetch yard devices');
                const json = await res.json();
                const options = (json?.devices || []).map((d) => {
                    const androidId = d.android_id ? String(d.android_id) : '';
                    const friendly = d.device_friendly_name || d.device_name || androidId;
                    const shortLabel = String(friendly || (d.asset_tag ? `Asset ${d.asset_tag}` : androidId));
                    const searchText = `${friendly || ''} ${androidId} ${d.asset_tag || ''}`.toLowerCase();
                    return {
                        value: androidId,
                        label: shortLabel,
                        searchText,
                    };
                });
                if (!cancelled) {
                    setSpeedDeviceOptions(options);
                    setSelectedSpeedDevice((prev) => {
                        if (!prev) return null;
                        return options.find((o) => o.value === prev.value) || null;
                    });
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('Speed-map yard device fetch error:', e);
                    setSpeedDeviceOptions([]);
                    setSelectedSpeedDevice(null);
                }
            } finally {
                if (!cancelled) setIsLoadingSpeedDevices(false);
            }
        };
        fetchDevicesForYard();
        return () => {
            cancelled = true;
        };
    }, [selectedYard, serverUrl, portalEmail]);

    
    useEffect(() => {
        const fetchAllData = async () => {
            if (!selectedYard || !serverUrl) return;

            setIsLoading(true);
            setError(null);
            const yardDetails = yardsData.find(y => y.Yard == selectedYard);
            if (!yardDetails) { setIsLoading(false); return; }

            const viewport = {
                latSW: yardDetails.lat - 0.05, lngSW: yardDetails.lng - 0.05,
                latNE: yardDetails.lat + 0.05, lngNE: yardDetails.lng + 0.05
            };

            const durationHours = selectedDuration === 'custom' ? null : (parseInt(selectedDuration, 10) || 168);

            // Always fetch both signal and speed data
            const fetchSignal = async () => {
                try {
                    const res = await fetch(`${serverUrl}/maiApi/heatmap-data`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            dataType: signalMetric,
                            yard_number: selectedYard,
                            android_id: selectedSpeedDevice?.value || undefined,
                            duration: selectedDuration !== 'custom' ? selectedDuration : undefined,
                            startDate: selectedDuration === 'custom' ? startDate : undefined,
                            endDate: selectedDuration === 'custom' ? endDate : undefined,
                            viewport,
                        }),
                    });
                    if (!res.ok) throw new Error('Signal API error');
                    return await res.json();
                } catch (e) {
                    console.error('Signal fetch error:', e);
                    return [];
                }
            };

            const fetchSpeed = async () => {
                try {
                    const body = { yard_number: selectedYard, mode: 'performance' };
                    if (selectedDuration === 'custom' && startDate && endDate) {
                        body.startDate = startDate;
                        body.endDate = endDate;
                    } else if (durationHours) {
                        body.durationHours = durationHours;
                    }
                    if (selectedSpeedDevice?.value) {
                        body.android_id = selectedSpeedDevice.value;
                    }
                    const res = await fetch(`${serverUrl}/maiApi/heatmap/speed-test`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    });
                    if (!res.ok) throw new Error('Speed test API error');
                    const json = await res.json();
                    return (json.points || []).map((p) => {
                        let value;
                        if (speedColorBy === 'upload') {
                            value = p.upload_mbps ?? 0;
                        } else if (speedColorBy === 'download') {
                            value = p.download_mbps ?? 0;
                        } else {
                            const up = p.upload_mbps ?? 0;
                            const down = p.download_mbps ?? 0;
                            value = (up + down) / 2;
                        }
                        return { ...p, value };
                    });
                } catch (e) {
                    console.error('Speed fetch error:', e);
                    return [];
                }
            };

            try {
                const [signalResult, speedResult] = await Promise.all([fetchSignal(), fetchSpeed()]);
                
                // Filter speed data by connection type
                const filteredSpeed = speedResult.filter((p) => {
                    const type = (p.connection_type || '').toLowerCase();
                    if (speedConnectionFilter === 'wifi') return type.includes('wifi');
                    if (speedConnectionFilter === 'cellular') return type.includes('cellular') || type.includes('mobile data');
                    return true;
                });

                setSignalData(signalResult || []);
                setSpeedData(filteredSpeed);
                
                // Set heatmapData based on current view mode
                if (viewMode === VIEW_SPEED) {
                    setHeatmapData(filteredSpeed);
                } else {
                    setHeatmapData(signalResult || []);
                }
                setTowerData([]);
            } catch (e) {
                setError(e.message);
                setHeatmapData([]);
                setSignalData([]);
                setSpeedData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [selectedYard, selectedDuration, viewMode, signalMetric, speedColorBy, speedConnectionFilter, startDate, endDate, serverUrl, selectedSpeedDevice]);

    const zoneStats = useMemo(
        () => computeZoneStats(heatmapData || [], drawnPolygon, viewMode, signalMetric),
        [heatmapData, drawnPolygon, viewMode, signalMetric]
    );

    const handleDrawPoint = (lat, lng) => {
        if (!drawModeEnabled) return;
        setDrawnPolygon((prev) => [...prev, [lat, lng]]);
    };

    const yardDetails = yardsData.find(y => y.Yard == selectedYard);
    const mapCenter = useMemo(() => {
        if (yardDetails) return [yardDetails.lat, yardDetails.lng];
        return [32.939052, -96.822724];
    }, [yardDetails]);

    return (
        <div className="p-4 sm:p-6 md:p-8 h-screen grid grid-rows-[auto_auto_1fr] gap-4">
            {/* Filter Bar */}
            <div className="bg-gray-100 p-4 rounded-lg shadow-md">
                 <div className="flex flex-wrap items-center gap-4">
                    <div className="min-w-[280px]">
                        <Select
                            options={yardOptions}
                            value={selectedYardOption}
                            onChange={(opt) => setSelectedYard(opt?.value ?? '')}
                            placeholder="Search yard..."
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }), menu: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <select value={selectedDuration} onChange={(e) => setSelectedDuration(e.target.value)} className="form-select border border-gray-300 rounded-md shadow-sm px-4 py-2">
                        {durationOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    {selectedDuration === 'custom' && (
  <div className="flex items-center space-x-2">
    <input
      type="date"
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
      className="border border-gray-300 rounded-md shadow-sm px-3 py-2"
    />
    <input
      type="date"
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
      className="border border-gray-300 rounded-md shadow-sm px-3 py-2"
    />
  </div>
)}
                    <div className="flex items-center gap-2 border-l border-gray-300 pl-2">
                        <span className="text-sm font-medium text-gray-700">Show:</span>
                        <label className="flex items-center text-sm">
                            <input type="radio" name="viewMode" checked={viewMode === VIEW_SIGNAL} onChange={() => setViewMode(VIEW_SIGNAL)} className="mr-1" />
                            Signal strength
                        </label>
                        <label className="flex items-center text-sm">
                            <input type="radio" name="viewMode" checked={viewMode === VIEW_SPEED} onChange={() => setViewMode(VIEW_SPEED)} className="mr-1" />
                            Internet speed
                        </label>
                    </div>
                    <div className="min-w-[320px]">
                        <Select
                            options={speedDeviceOptions}
                            value={selectedSpeedDevice}
                            onChange={(opt) => setSelectedSpeedDevice(opt || null)}
                            placeholder={isLoadingSpeedDevices ? 'Loading devices...' : 'Search device by asset or friendly name...'}
                            filterOption={(option, inputValue) => {
                                const q = String(inputValue || '').toLowerCase().trim();
                                if (!q) return true;
                                return String(option?.data?.searchText || option?.label || '').includes(q);
                            }}
                            isSearchable
                            isClearable
                            isDisabled={!selectedYard || isLoadingSpeedDevices}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }), menu: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    {viewMode === VIEW_SIGNAL && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Metric:</span>
                        <select value={signalMetric} onChange={(e) => setSignalMetric(e.target.value)} className="form-select border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm">
                            {SIGNAL_METRICS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>
                    )}
                    {viewMode === VIEW_SPEED && (
                        <>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Color by:</span>
                                <select
                                    value={speedColorBy}
                                    onChange={(e) => setSpeedColorBy(e.target.value)}
                                    className="form-select border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm"
                                >
                                    {SPEED_COLOR_BY.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Connection:</span>
                                <select
                                    value={speedConnectionFilter}
                                    onChange={(e) => setSpeedConnectionFilter(e.target.value)}
                                    className="form-select border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm"
                                >
                                    {SPEED_CONNECTION_FILTERS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                        </>
                    )}
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500" /> Worst
                        <span className="inline-block w-3 h-3 rounded-full bg-green-500 ml-1" /> Best
                    </div>
                </div>
            </div>

            {/* Address Bar */}
            {yardDetails && (
                <div className="bg-white p-3 rounded-lg shadow text-center text-sm font-bold text-black">
                    Address: {yardDetails.Address}, Phone: {yardDetails.Phone}, Fax: {yardDetails.Fax}, Lat: {yardDetails.lat} Lng: {yardDetails.lng}
                </div>
            )}
            
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
                    {error}
                </div>
            )}
            {/* Map Area */}
            <div className="w-full rounded-lg shadow-lg overflow-hidden relative min-h-0">
                {isLoading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001]">
                        <div className="text-white text-xl">Loading Map Data...</div>
                    </div>
                )}
                <LeafletMapComponent
                    center={mapCenter}
                    zoom={19}
                    data={heatmapData}
                    dataType={viewMode}
                    signalMetric={viewMode === VIEW_SIGNAL ? signalMetric : undefined}
                    speedColorBy={viewMode === VIEW_SPEED ? speedColorBy : undefined}
                    towerData={towerData}
                    drawModeEnabled={drawModeEnabled}
                    drawnPolygon={drawnPolygon}
                    onDrawPoint={handleDrawPoint}
                    zoneStats={zoneStats}
                    onToggleDrawMode={setDrawModeEnabled}
                    onClearPolygon={() => setDrawnPolygon([])}
                    isPenActive={isPenActive}
                    onSetPenActive={setIsPenActive}
                    allSignalData={signalData}
                    allSpeedData={speedData}
                    yardTileUrl={yardTileUrl}
                />
            </div>
        </div>
    );
};

export default HeatMap;